import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from './firestore-compat';
import { useNavigate } from 'react-router-dom';
import './App.css';

function Notificacoes({ isEmbedded = false }) {
    const [notificacoes, setNotificacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Escutar notificações em tempo real
        const q = query(
            collection(db, 'notificacoes'),
            where('destinatarioUid', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Ordenar por data (mais recentes primeiro)
            notifs.sort((a, b) => {
                const dateA = new Date(a.dataNotificacao);
                const dateB = new Date(b.dataNotificacao);
                return dateB - dateA;
            });

            setNotificacoes(notifs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleMarcarLida = async (notifId) => {
        try {
            await updateDoc(doc(db, 'notificacoes', notifId), {
                lida: true
            });
        } catch (error) {
            console.error('Erro ao marcar notificação como lida:', error);
        }
    };

    const handleExcluirNotificacao = async (notifId) => {
        if (!window.confirm('Deseja excluir esta notificação?')) return;

        try {
            await deleteDoc(doc(db, 'notificacoes', notifId));
        } catch (error) {
            console.error('Erro ao excluir notificação:', error);
            alert('Erro ao excluir notificação.');
        }
    };

    const handleReagendar = (notif) => {
        // Redirecionar para a respectiva agenda (pessoal/qualidade/direção/homecare) baseada na collection do convite?
        // O modal antes apenas emitia um callback onReagendar(eventoId, eventoCollection) que abria o modal.
        // Como agora estamos numa página genérica de Notificações, não temos o AddAuditoriaModal pronto na tela.
        // Uma solução é redirecionar o usuário para a agenda onde o evento mora, e lá ele pode editá-lo.

        // Exemplo simples: Ir para a Minha Agenda Pessoal onde ele visualiza todos
        navigate('/minha-agenda');
    };

    const notificacoesNaoLidas = notificacoes.filter(n => !n.lida);
    const notificacoesLidas = notificacoes.filter(n => n.lida);

    return (
        <div className={isEmbedded ? "" : "container-fluid p-4"}>
            {!isEmbedded && (
                <div className="page-header mb-4">
                    <h2 style={{ color: '#555', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        🔔 Minhas Notificações
                        {notificacoesNaoLidas.length > 0 && (
                            <span style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                borderRadius: '50%',
                                padding: '4px 10px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                lineHeight: '1.2'
                            }}>
                                {notificacoesNaoLidas.length}
                            </span>
                        )}
                    </h2>
                    <p className="text-muted">Acompanhe recusas e alterações em seus convites de agenda.</p>
                </div>
            )}

            <div style={{ backgroundColor: isEmbedded ? 'transparent' : '#fff', borderRadius: '8px', padding: isEmbedded ? '0' : '20px', boxShadow: isEmbedded ? 'none' : '0 2px 4px rgba(0,0,0,0.05)', minHeight: '60vh' }}>
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : notificacoes.length === 0 ? (
                    <div className="text-center p-5 text-muted">
                        <h4 style={{ color: '#ccc' }}>Nenhuma notificação encontrada</h4>
                        <p>Você não possui notificações no momento.</p>
                    </div>
                ) : (
                    <div>
                        {notificacoesNaoLidas.length > 0 && (
                            <div className="mb-4">
                                <h5 style={{ color: '#333', borderBottom: '2px solid #e67e22', paddingBottom: '5px', marginBottom: '15px' }}>
                                    Não Lidas
                                </h5>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {notificacoesNaoLidas.map(notif => (
                                        <NotificationCard
                                            key={notif.id}
                                            notif={notif}
                                            onMarcarLida={() => handleMarcarLida(notif.id)}
                                            onReagendar={() => handleReagendar(notif)}
                                            onExcluir={() => handleExcluirNotificacao(notif.id)}
                                            isLida={false}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {notificacoesLidas.length > 0 && (
                            <div>
                                <h5 style={{ color: '#333', borderBottom: '2px solid #ccc', paddingBottom: '5px', marginBottom: '15px' }}>
                                    Anteriores
                                </h5>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {notificacoesLidas.map(notif => (
                                        <NotificationCard
                                            key={notif.id}
                                            notif={notif}
                                            onExcluir={() => handleExcluirNotificacao(notif.id)}
                                            isLida={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function NotificationCard({ notif, onMarcarLida, onReagendar, onExcluir, isLida }) {
    const dataObj = new Date(notif.dataNotificacao);
    const dataFormatada = dataObj.toLocaleDateString('pt-BR');
    const horaFormatada = dataObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    let icone = '📌';
    let corDestaque = '#ffeaa7'; // amarelo suave default

    if (notif.tipo === 'recusa_convite') {
        icone = '❌';
        corDestaque = '#ff7675'; // vermelho claro
    } else if (notif.tipo === 'mudanca_agendamento' || notif.tipo === 'alteracao_agendamento') {
        icone = '🔄';
        corDestaque = '#74b9ff'; // azul claro
    } else if (notif.tipo === 'reagendado') {
        icone = '📅';
        corDestaque = '#f3a683'; // laranja suave
    }

    // Reconstruir título e mensagem se não existirem (para retrocompatibilidade e novos convites)
    let tituloExibicao = notif.titulo;
    let mensagemExibicao = notif.mensagem;

    if (!tituloExibicao && notif.tipo === 'recusa_convite') {
        tituloExibicao = `Convite Recusado: ${notif.eventoDescricao || notif.eventoTitulo || 'Evento'}`;
        mensagemExibicao = `${notif.recusadoPorNome || 'Um participante'} recusou o seu convite para o evento do dia ${notif.eventoData || '?'} às ${notif.eventoHorario || '?'}.`;
    } else if (!tituloExibicao && (notif.tipo === 'mudanca_agendamento' || notif.tipo === 'alteracao_agendamento')) {
        tituloExibicao = `Alteração de Horário: ${notif.eventoTitulo || notif.eventoDescricao || 'Evento'}`;
        mensagemExibicao = `${notif.alteradoPor || 'Um participante'} alterou a data/horário deste evento.\nDe: ${notif.dataAntiga || '?'} às ${notif.horarioAntigo || '?'}\nPara: ${notif.dataNova || '?'} às ${notif.horarioNovo || '?'}`;
    } else if (!tituloExibicao) {
        tituloExibicao = 'Nova Notificação';
        mensagemExibicao = 'Você tem uma nova atualização.';
    }

    return (
        <div style={{
            background: isLida ? '#fff' : '#fafafa',
            border: '1px solid #eaeaea',
            borderLeft: `4px solid ${isLida ? '#ddd' : corDestaque}`,
            borderRadius: '4px',
            padding: '10px 14px',
            position: 'relative',
            opacity: isLida ? 0.75 : 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
                    <span>{icone}</span>
                    {tituloExibicao}
                </strong>
                <span style={{ fontSize: '11px', color: '#999' }}>
                    {dataFormatada} às {horaFormatada}
                </span>
            </div>

            <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.4', paddingLeft: '22px' }}>
                <span style={{ whiteSpace: 'pre-wrap' }}>{mensagemExibicao}</span>

                {notif.justificativa && (
                    <div style={{
                        marginTop: '6px',
                        background: '#f8f9fa',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        borderLeft: '2px solid #ccc',
                        fontStyle: 'italic',
                        color: '#555'
                    }}>
                        " {notif.justificativa} "
                    </div>
                )}
            </div>

            {/* Ações (botões minimalistas) */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '4px' }}>
                {notif.tipo === 'recusa_convite' && !isLida && (
                    <button
                        onClick={onReagendar}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#0984e3',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '0',
                            textDecoration: 'none'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                        Abrir Agenda
                    </button>
                )}

                {!isLida && (
                    <button
                        onClick={onMarcarLida}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#00b894',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '0',
                            textDecoration: 'none'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                        ✓ Lida
                    </button>
                )}

                <button
                    onClick={onExcluir}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#d63031',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: '0',
                        textDecoration: 'none'
                    }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    title="Excluir notificação"
                >
                    Excluir
                </button>
            </div>
        </div>
    );
}

export default Notificacoes;
